import React, { useState, useRef, useEffect } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from '../../authContext/authContext';
import { markFormScanned } from '../../Services/apiService';
import '../../styles/QRScanner.css'; // We'll create this for custom styles

function QRScanner() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [isScanning, setIsScanning] = useState(false);
  const [detectedLabTestId, setDetectedLabTestId] = useState('');
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionState, setPermissionState] = useState('prompt');
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [detectionCount, setDetectionCount] = useState(0);
  const [alertMessage, setAlertMessage] = useState({ type: '', message: '', show: false });
  const [lastDetectionTime, setLastDetectionTime] = useState(0);
  const [scannerInitialized, setScannerInitialized] = useState(false);
  const [nextScanTimer, setNextScanTimer] = useState(0);
  
  // Use a ref to track processing state synchronously
  const isProcessingRef = useRef(false);
  
  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const codeReaderRef = useRef(null);

  // Check if device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  // Beep sound function
  const playBeepSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (err) {
      // Silent audio error
    }
  };

  // Extract lab test ID from URL
  const extractLabTestId = (url) => {
    try {
      const urlPattern = /\/managePatientForms\/form\/([a-f0-9-]{36})/i;
      const match = url.match(urlPattern);
      return match ? match[1] : null;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    checkCameraPermission();
    getAvailableCameras();
    
    return () => {
      stopScanning();
      
      try {
        if (codeReaderRef.current) {
          codeReaderRef.current.reset();
        }
      } catch (err) {
        // Silent cleanup
      }
    };
  }, []);

  // Timer countdown effect
  useEffect(() => {
    let interval;
    if (lastDetectionTime > 0 && isScanning) {
      interval = setInterval(() => {
        const currentTime = Date.now();
        const timeSinceLastScan = currentTime - lastDetectionTime;
        const remaining = Math.max(0, 3000 - timeSinceLastScan);
        
        if (remaining > 0) {
          setNextScanTimer(Math.ceil(remaining / 1000));
        } else {
          setNextScanTimer(0);
        }
      }, 100);
    } else {
      setNextScanTimer(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [lastDetectionTime, isScanning]);

  // Check camera permission status
  const checkCameraPermission = async () => {
    try {
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'camera' });
        setPermissionState(permission.state);
        
        permission.onchange = () => {
          setPermissionState(permission.state);
        };
      }
    } catch (err) {
      // Permission API not supported
    }
  };

  // Get list of available cameras
  const getAvailableCameras = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          stream.getTracks().forEach(track => track.stop());
        })
        .catch(() => {});

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setCameras(videoDevices);
      
      if (videoDevices.length === 0) {
        setError('No cameras found on this device.');
      }
    } catch (err) {
      setError('Unable to access camera devices.');
    }
  };

  // Start camera stream
  const startCamera = async () => {
    try {
      setIsLoading(true);
      setError('');
      setIsScanning(true);
      
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported by your browser');
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          facingMode: isMobile 
            ? (currentCameraIndex === 0 ? 'environment' : 'user')
            : true,
          width: { ideal: 1920, min: 640 },
          height: { ideal: 1080, min: 480 },
          frameRate: { ideal: 30, min: 15 }
        }
      };

      if (!isMobile && cameras.length > 0 && cameras[currentCameraIndex]) {
        constraints.video.deviceId = { exact: cameras[currentCameraIndex].deviceId };
      }
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (!videoRef.current) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play().catch(e => {
              setError('Failed to start video playback.');
            });
          }
        };
        
        videoRef.current.oncanplay = () => {
          setPermissionState('granted');
        };
        
        videoRef.current.load();
      } else {
        setError('Video element not found. Please refresh and try again.');
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        setIsScanning(false);
        return;
      }
      
      showAlert('success', 'Camera started successfully!');
      
      // Initialize QR scanner once camera is started
      if (!scannerInitialized) {
        setTimeout(() => {
          initializeQRScanner();
        }, 500);
      }
      
    } catch (err) {
      let errorMessage = 'Unable to access camera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera permission denied. Please allow camera access.';
        setPermissionState('denied');
        setShowPermissionModal(true);
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is being used by another application.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Stop camera stream
  const stopScanning = () => {
    try {
      if (codeReaderRef.current) {
        codeReaderRef.current.reset();
      }
    } catch (err) {
      // Silent cleanup
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    setIsScanning(false);
    setScannerInitialized(false);
    setDetectionCount(0);
    setLastDetectionTime(0);
    setDetectedLabTestId('');
    isProcessingRef.current = false; // Reset processing ref
  };

  // Switch between cameras
  const switchCamera = () => {
    if (isMobile) {
      setCurrentCameraIndex(prev => prev === 0 ? 1 : 0);
    } else {
      setCurrentCameraIndex(prev => (prev + 1) % cameras.length);
    }
    
    if (isScanning) {
      stopScanning();
      setTimeout(() => startCamera(), 100);
    }
  };

  // Initialize QR scanner
  const initializeQRScanner = async () => {
    if (!videoRef.current || scannerInitialized) return;

    try {
      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;
      
      setScannerInitialized(true);
      
      codeReader.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        (result, error) => {
          if (result) {
            const qrData = result.getText();
            const currentTime = Date.now();
            
            // Prevent any detections within 5 seconds OR during processing (using ref for immediate sync check)
            if (isProcessingRef.current || isProcessing || (currentTime - lastDetectionTime < 3000)) {
              return;
            }
            
            // Immediately set processing flag to prevent race conditions
            isProcessingRef.current = true;
            setLastDetectionTime(currentTime);
            
            // Check if it's a valid URL
            if (isValidUrl(qrData)) {
              // Extract lab test ID
              const labTestId = extractLabTestId(qrData);
              if (labTestId) {
                // Play beep sound
                playBeepSound();
                
                // Set detected data but don't pause scanning
                setDetectedLabTestId(labTestId);
                
                // Process form data while keeping scanner active
                processFormData(labTestId);
                
                return;
              } else {
                // Reset the flag if invalid URL
                isProcessingRef.current = false;
                showAlert('warning', 'QR code URL does not contain a valid lab test ID');
              }
            } else {
              // Reset the flag if invalid URL
              isProcessingRef.current = false;
              showAlert('warning', 'QR code does not contain a valid URL');
            }
          }
          
          if (error && !error.message.includes('No MultiFormat Readers')) {
            // Silent error handling for scanning errors
          }
        }
      );
      
    } catch (err) {
      setError('Failed to initialize QR scanner.');
    }
  };

  // Validate URL
  const isValidUrl = (string) => {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  // Process form data using detected lab test ID
  const processFormData = async (labTestId) => {
    // Double-check: Prevent multiple simultaneous requests
    if (isProcessing) {
      isProcessingRef.current = false; // Reset ref if already processing
      return;
    }
    
    setIsProcessing(true);
    
    try {
      await markFormScanned(labTestId);

      console.log('✅ Successfully processed:', labTestId);

      showAlert('success', `✅ Successfully scanned: ${labTestId}`);

      // Increment count only on successful scan
      setDetectionCount(prev => prev + 1);

      // Don't pause scanning - keep it active for next scan
      // Just reset the current detection state (but keep the count)
      // Don't reset lastDetectionTime to maintain 5-second cooldown
      setDetectedLabTestId('');

    } catch (error) {
      // Don't set successful request on error - allow retry
      let errorMessage = 'Failed to fetch form data';
      if (error.status === 404) {
        errorMessage = `Form not found for Lab Test ID: ${labTestId}. Please check the QR code.`;
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.status === 401) {
        errorMessage = 'Unauthorized access. Please login first.';
      } else if (error.status === 403) {
        errorMessage = 'You do not have permission to scan forms. Admin or operator role required.';
      }

      showAlert('danger', `❌ ${errorMessage}`);
      
      // Reset detection state for retry with same or different ID (but keep the count)
      // Don't reset lastDetectionTime to maintain 5-second cooldown
      setDetectedLabTestId('');
      
    } finally {
      setIsProcessing(false);
      isProcessingRef.current = false; // Reset the ref flag
      // Keep scanning active - don't pause it
    }
  };

  // Show alert message
  const showAlert = (type, message) => {
    setAlertMessage({ type, message, show: true });
    setTimeout(() => {
      setAlertMessage(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // Handle back button click
  const handleBackClick = () => {
    stopScanning(); // Stop camera and cleanup
    navigate('/dashboard'); // Navigate to dashboard
  };

  // Check authentication when component loads
  useEffect(() => {
    if (!loading && !user) {
      showAlert('warning', 'Please login to access the QR scanner.');
    }
  }, [loading, user]);

  return (
    <div className="container-fluid min-vh-100 bg-light py-4">
      {/* Back Button */}
      <div className="row">
        <div className="col-12">
          <button 
            className="btn btn-outline-secondary mb-3 ms-3"
            onClick={handleBackClick}
            style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 1050 }}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="row justify-content-center mb-4"
           style={{ marginTop: '60px' }}>
        <div className="col-12 col-md-8 col-lg-6 text-center">
          <h2 className="fw-bold mb-3 d-flex align-items-center justify-content-center">
            <i className="bi bi-qr-code-scan me-2 fs-1"></i>
            QR Code Scanner
          </h2>
          <p className="text-muted">Scan QR codes to process automated requests</p>
          
          {/* User Status Indicator */}
          {!loading && (
            <div className="mt-2">
              {user ? (
                <span className="badge bg-success">
                  <i className="bi bi-person-check me-1"></i>
                  Signed in as {user.email}
                </span>
              ) : (
                <span className="badge bg-warning text-dark">
                  <i className="bi bi-person-x me-1"></i>
                  Not signed in
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Alert Messages */}
      {alertMessage.show && (
        <div className="row justify-content-center mb-3">
          <div className="col-12 col-md-8 col-lg-6">
            <div className={`alert alert-${alertMessage.type} alert-dismissible fade show`} role="alert">
              {alertMessage.message}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setAlertMessage(prev => ({ ...prev, show: false }))}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="row justify-content-center mb-3">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setError('')}
              ></button>
              {error.includes('permission') && (
                <div className="mt-2">
                  <button 
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => setShowPermissionModal(true)}
                  >
                    Show Help
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Permission Warning */}
      {permissionState === 'denied' && !error && (
        <div className="row justify-content-center mb-3">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="alert alert-warning" role="alert">
              Camera access was denied. Please enable camera permissions.
              <button 
                className="btn btn-outline-warning btn-sm ms-2"
                onClick={() => setShowPermissionModal(true)}
              >
                Show Instructions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Scanner Card */}
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              
              {/* Start Scanning Interface */}
              {!isScanning && !isProcessing && (
                <div className="text-center">
                  <div className="mb-4">
                    <i className="bi bi-qr-code-scan display-1 text-primary mb-3"></i>
                    <h4 className="mb-3">
                      {loading ? 'Loading...' : user ? 'Ready to Scan' : 'Authentication Required'}
                    </h4>
                    <p className="text-muted mb-4">
                      {loading ? (
                        'Checking authentication status...'
                      ) : user ? (
                        <>
                          Click the button below to start scanning QR codes with URLs.
                          {!navigator.mediaDevices && (
                            <span className="d-block text-danger mt-2">
                              ⚠️ Camera access requires HTTPS or localhost
                            </span>
                          )}
                        </>
                      ) : (
                        'Please sign in to access the QR scanner and process automated requests.'
                      )}
                    </p>
                  </div>

                  <div className="d-grid gap-2 d-md-block">
                    <button
                      className="btn btn-primary btn-lg px-4 py-2"
                      onClick={startCamera}
                      disabled={isLoading || loading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Starting Camera...
                        </>
                      ) : loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Loading...
                        </>
                      ) : user ? (
                        <>
                          <i className="bi bi-camera me-2"></i>
                          Start Scanning
                        </>
                      ) : (
                        <>
                          <i className="bi bi-shield-lock me-2"></i>
                          Sign In to Scan
                        </>
                      )}
                    </button>

                    {permissionState === 'denied' && (
                      <button
                        className="btn btn-outline-primary btn-lg px-4 py-2 ms-md-2"
                        onClick={startCamera}
                        disabled={isLoading || loading}
                      >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        {user ? 'Retry Camera Access' : 'Sign In & Retry Camera'}
                      </button>
                    )}
                  </div>

                  {cameras.length > 0 && (
                    <p className="text-muted mt-3 mb-0">
                      {cameras.length} camera{cameras.length > 1 ? 's' : ''} detected
                    </p>
                  )}
                </div>
              )}

              {/* Processing State */}
              {isProcessing && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}></div>
                  <h4 className="mb-3">Processing Form Data...</h4>
                  <p className="text-muted">Fetching form data for Lab Test ID: {detectedLabTestId}</p>
                </div>
              )}

              {/* Video Scanner */}
              {isScanning && (
                <div className="position-relative">
                  <div className="scanner-container position-relative bg-dark rounded mb-3 overflow-hidden">
                    <video
                      ref={videoRef}
                      className="w-100 h-auto scanner-video"
                      style={{ minHeight: '300px', maxHeight: '500px' }}
                      playsInline
                      muted
                      autoPlay
                    />
                    
                    {/* Scanning overlay */}
                    <div className="scanner-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                      {(!videoRef.current || videoRef.current.readyState < 4) && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-75 text-white">
                          <div className="text-center">
                            <div className="spinner-border mb-3" role="status"></div>
                            <p>Loading camera feed...</p>
                          </div>
                        </div>
                      )}
                      
                      {/* QR Scanning frame */}
                      {videoRef.current?.readyState === 4 && (
                        <div className={`qr-scanner-frame ${isProcessing ? 'processing' : ''}`}></div>
                      )}
                    </div>
                  </div>

                  {/* Scanner Controls */}
                  <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
                    <button
                      className="btn btn-danger"
                      onClick={stopScanning}
                    >
                      <i className="bi bi-stop-fill me-1"></i>
                      Stop Scanning
                    </button>
                    
                    {(cameras.length > 1 || isMobile) && (
                      <button
                        className="btn btn-outline-primary"
                        onClick={switchCamera}
                      >
                        <i className="bi bi-camera-reels me-1"></i>
                        Switch Camera
                      </button>
                    )}
                  </div>



                  <div className="text-center mt-3">
                    {nextScanTimer > 0 && (
                      <p className="fs-6 text-muted mb-2">
                        Next scan available in: <span className="fw-bold text-warning">{nextScanTimer}s</span>
                      </p>
                    )}
                    <p className={`fs-5 fw-bold ${isProcessing ? "text-warning" : "text-primary"}`}>
                      {isProcessing ? "Processing request..." : `Scanned: ${detectionCount}`}
                    </p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Permission Help Modal */}
      {showPermissionModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-camera text-primary me-2"></i>
                  Camera Permission Help
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowPermissionModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <h6>How to Enable Camera Access</h6>
                
                <div className="mb-4">
                  <h6 className="text-primary">🔒 For HTTPS Security:</h6>
                  <p>Camera access requires a secure connection (HTTPS) or localhost.</p>
                </div>

                <div className="mb-4">
                  <h6 className="text-primary">🌐 Browser Permission Steps:</h6>
                  
                  <div className="mb-3">
                    <strong>Chrome/Edge:</strong>
                    <ol>
                      <li>Click the camera icon in the address bar</li>
                      <li>Select "Always allow" for camera access</li>
                      <li>Refresh the page and try again</li>
                    </ol>
                  </div>
                  
                  <div className="mb-3">
                    <strong>Firefox:</strong>
                    <ol>
                      <li>Click the shield icon in the address bar</li>
                      <li>Click "Turn off Blocking for This Site"</li>
                      <li>Refresh and allow camera when prompted</li>
                    </ol>
                  </div>
                  
                  <div className="mb-3">
                    <strong>Safari:</strong>
                    <ol>
                      <li>Go to Safari → Preferences → Websites</li>
                      <li>Select "Camera" from the left sidebar</li>
                      <li>Set this website to "Allow"</li>
                    </ol>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="text-primary">📱 Mobile Device Steps:</h6>
                  <ol>
                    <li>Ensure you're using a modern browser</li>
                    <li>When prompted, tap "Allow" for camera access</li>
                    <li>Check your device settings if no prompt appears</li>
                  </ol>
                </div>

                <div className="alert alert-info">
                  <strong>Still having issues?</strong> Try refreshing the page, clearing your browser cache, or using a different browser.
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowPermissionModal(false)}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => {
                    setShowPermissionModal(false);
                    startCamera();
                  }}
                >
                  <i className="bi bi-camera me-1"></i>
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QRScanner;
